import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'

import GlobalLoaderOrSpinner from 'app/components/Loaders/GlobalLoaderOrSpinner'
import Review360 from 'app/components/SVGS/Review360.jsx'
import { getUserFullName } from 'helpers/getUserUIData'
import BarChart from './BarChart'
import RadarChart from './RadarChart'
import Section from './Section'

import './styles.scss'

const bc = 'reports'

export const COLORS_OPACITY = [
  'rgba(241, 195, 49, 0.4)',
  'rgba(45, 216, 120, 0.4)',
  'rgba(60, 120, 216, 0.4)',
  'rgba(102, 78, 167, 0.4)',
]

export const COLORS = [
  'rgba(241, 195, 49, 1)',
  'rgba(45, 216, 120, 1)',
  'rgba(60, 120, 216, 1)',
  'rgba(102, 78, 167, 1)',
]

const breakPage = (count) => {
  return count !== 0 && count % 4 === 0 ? <div className="page-break" /> : null;
}

const renderBarChartForEachQuestion = byEachQuestion => {
  return byEachQuestion.map(({ title, result }, i) => (
    <Fragment>
      <BarChart data={result} title={title} key={`${i}_each_question`} />
      { breakPage(i) }
    </Fragment>
  ))
}

const renderOverallAvgByCompetency = overallAvgByCompetency => {
  return overallAvgByCompetency.map(({ competency, overall, byAllQuestions, byEachQuestion }, i) => (
    <Fragment key={`${competency}_${i}`}>
      <BarChart
        data={byAllQuestions}
        title={`${competency} Overall Rating: ${typeof overall === 'number' ? overall.toFixed(1) : overall}`}
        subtitle="Aggregate ratings for all competencies in review"
      />
      {byEachQuestion && renderBarChartForEachQuestion(byEachQuestion)}
    </Fragment>
  ))
}

const renderAnswersToEachQuestion = answersToEachQuestion => {
  return answersToEachQuestion.map(({ question, answers }, i) => (
    <Section question={question} answers={answers} key={`${question}_${i}`} />
  ))
}

const Reports = ({ report, isPending }) => {
  if (isPending) {
    return <GlobalLoaderOrSpinner />
  }

  const reviewersAvgByCompetency = report.get('reviewersAvgByCompetency')
  const overallAvgByReviewerType = report.get('overallAvgByReviewerType')
  const overallAvgByCompetency = report.get('overallAvgByCompetency')
  const answersToEachQuestion = report.get('answersToEachQuestion')

  return (
    <div className={bc}>
      <div className={`${bc}__wrapper`}>
        <div className={`${bc}__content`}>
          <div className={`${bc}__header`}>
            <Review360 />
            <span className={`${bc}__title-s1`}>360 Review Summary</span>
            <span className={`${bc}__subtitle`}>
              {getUserFullName(report.get('firstName'), report.get('lastName'), report.get('email'))}
            </span>
          </div>

          <div className={`${bc}__radar`}>
            {reviewersAvgByCompetency && <RadarChart data={reviewersAvgByCompetency} />}
          </div>
          
          <div className={`${bc}__overall`}>
            {overallAvgByReviewerType && (
              <BarChart data={overallAvgByReviewerType} title="Overall Rating" classNameTitle={`${bc}__title-s2`} />
            )}
          </div>
          
          <div className="page-break" />
          
          {overallAvgByCompetency && renderOverallAvgByCompetency(overallAvgByCompetency)}
          {answersToEachQuestion && renderAnswersToEachQuestion(answersToEachQuestion)}
        </div>
        <div className={`${bc}__print-name`}>{`${getUserFullName(report.get('firstName'), report.get('lastName'), report.get('email'))}`}</div>
        <div className={`${bc}__copyright`}>{`Copyright ${new Date().getFullYear()}. 360 review`}</div>
      </div>
    </div>
  )
}

const estimate = PropTypes.shape({
  reviewerType: PropTypes.oneOf(['Self', 'Direct', 'Peers', 'Supervisors']),
  avg: PropTypes.number,
  teamAvg: PropTypes.number,
})

Reports.propTypes = {
  isPending: PropTypes.bool.isRequired,
  report: ImmutablePropTypes.mapContains({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    reviewersAvgByCompetency: PropTypes.arrayOf(
      PropTypes.shape({
        competency: PropTypes.string.isRequired,
        Direct: PropTypes.number.isRequired,
        Peers: PropTypes.number.isRequired,
        Self: PropTypes.number.isRequired,
        Supervisors: PropTypes.number.isRequired,
      })
    ),
    overallAvgByReviewerType: PropTypes.arrayOf(estimate),
    overallAvgByCompetency: PropTypes.arrayOf(
      PropTypes.shape({
        competency: PropTypes.string,
        overall: PropTypes.number,
        byAllQuestions: PropTypes.arrayOf(estimate),
        byEachQuestion: PropTypes.arrayOf(
          PropTypes.shape({
            title: PropTypes.string,
            result: PropTypes.arrayOf(estimate),
          })
        ),
      })
    ),
    answersToEachQuestion: PropTypes.arrayOf(
      PropTypes.shape({
        question: PropTypes.string,
        answers: PropTypes.shape({
          Self: PropTypes.number,
          Supervisors: PropTypes.arrayOf(PropTypes.number),
          Peers: PropTypes.arrayOf(PropTypes.number),
          Direct: PropTypes.arrayOf(PropTypes.number),
        }),
      })
    ),
  }).isRequired,
}

export default Reports
